<template>
  <div class="folder-page">
    <!-- 标题区域 -->
    <div class="page-title-area">
      <h1 class="page-title">我的收藏</h1>
      <p class="page-desc">
        按收藏夹管理案例法规、法律文书、检索报告与企业查询结果。
      </p>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <a-input
        v-model:value="searchKey"
        class="folder-search"
        placeholder="搜索收藏夹、收藏内容"
        @pressEnter="handleSearch"
      >
        <template #suffix>
          <img
            src="@/assets/icons/i-search.png"
            style="width: 20px; height: 20px; cursor: pointer"
            @click="handleSearch"
          />
        </template>
      </a-input>
      <a-button type="primary" @click="handleCreateFolder">新建收藏夹</a-button>
    </div>

    <!-- 收藏夹卡片网格 -->
    <div class="folder-grid">
      <div
        v-for="item in folderList"
        :key="item.id"
        class="folder-card"
        @click="$emit('openFolder', item)"
      >
        <div class="card-top">
          <div class="folder-icon">
            <img
              src="@/assets/icons/i-collect.png"
              style="width: 36px; height: 36px"
            />
            <div class="dash-box"></div>
          </div>
          <span class="count-num">{{ item.totalCount || 0 }}</span>
        </div>
        <div class="folder-name">{{ item.name }}</div>
        <div class="folder-sub">
          <span>{{ item.createTime || "" }}</span>
          <a-tooltip v-if="item.name !== '默认收藏'" title="删除">
            <DeleteOutlined
              class="folder-delete-icon"
              @click.stop="handleDelete(item)"
            />
          </a-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, h, onMounted } from "vue";
import { Modal, message } from "ant-design-vue";
import { DeleteOutlined } from "@ant-design/icons-vue";
import {
  getFolderList,
  createFolder,
  deleteFolderApi,
} from "../../api/collect/folder";

const emit = defineEmits(["openFolder"]);
const searchKey = ref("");

// 文件夹列表
const folderList = ref([]);

// 获取收藏夹列表
const fetchFolderList = async () => {
  try {
    const res = await getFolderList(searchKey.value);
    if (res?.data) {
      folderList.value = res.data;
    }
  } catch (err) {
    console.error("获取收藏夹失败", err);
  }
};

// 搜索
const handleSearch = () => {
  fetchFolderList();
};

// 新建收藏夹
const handleCreateFolder = () => {
  let folderName = "";
  Modal.confirm({
    title: "新建收藏夹",
    content: h("div", [
      h(
        "p",
        { style: "margin-bottom:8px;color:#666;font-size:14px" },
        "请输入收藏夹名称",
      ),
      h("input", {
        style:
          "width:100%;height:36px;border:1px solid #d9d9d9;border-radius:6px;padding:0 12px;font-size:14px;outline:none;box-sizing:border-box",
        placeholder: "例如：类案检索",
        onInput: (e) => {
          folderName = e.target.value;
        },
      }),
    ]),
    okText: "确定",
    cancelText: "取消",
    onOk: async () => {
      if (!folderName || !folderName.trim()) {
        message.warning("请输入收藏夹名称");
        return false;
      }
      try {
        await createFolder(folderName.trim(), 0);
        message.success("创建成功");
        fetchFolderList();
      } catch (err) {
        console.error("创建收藏夹失败", err);
        return false;
      }
    },
  });
};

// 删除收藏夹
const handleDelete = (item) => {
  Modal.confirm({
    title: "删除收藏夹",
    content: `确定删除收藏夹「${item.name}」吗？删除后不可恢复。`,
    okText: "确定",
    okType: "danger",
    cancelText: "取消",
    onOk: async () => {
      try {
        await deleteFolderApi(item.id);
        message.success("删除成功");
        fetchFolderList();
      } catch (err) {
        console.error("删除收藏夹失败", err);
        message.error("删除失败: " + (err.message || "未知错误"));
      }
    },
  });
};

onMounted(() => {
  fetchFolderList();
});
</script>

<style scoped lang="less">
@pageBg: #f5f7fc;
@cardWhite: #ffffff;
@textMain: #1d2129;
@textGray: #86909c;
@primaryBlue: #4080ff;
@borderLine: #e5e7eb;
@radiusSm: 6px;
@radiusMd: 10px;

.folder-page {
  width: 100%;
  .page-title-area {
    margin-bottom: 26px;
    .page-title {
      font-size: 28px;
      font-weight: 600;
      color: @textMain;
      margin: 0 0 6px;
    }
    .page-desc {
      font-size: 14px;
      color: @textGray;
      margin: 0;
    }
  }

  .search-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: @cardWhite;
    padding: 16px 22px;
    border-radius: @radiusMd;
    margin-bottom: 24px;
    .folder-search {
      width: 340px;
      :deep(.ant-input-affix-wrapper) {
        border-radius: @radiusSm !important;
      }
    }
  }

  .folder-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 16px;
  }

  .folder-card {
    position: relative;
    background: @cardWhite;
    border: 1px solid @borderLine;
    border-radius: @radiusMd;
    padding: 20px;
    cursor: pointer;
    transition: 0.2s;
    &:hover {
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
    }
    .card-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
      .folder-icon {
        position: relative;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        .dash-box {
          position: absolute;
          inset: -3px;
          border: 1px dashed #c0c4cc;
          border-radius: 6px;
        }
      }
      .count-num {
        font-size: 16px;
        color: @textGray;
      }
    }
    .folder-name {
      font-size: 16px;
      font-weight: 600;
      color: @textMain;
      margin-bottom: 8px;
    }
    .folder-sub {
      font-size: 13px;
      color: @textGray;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
}

.folder-delete-icon {
  font-size: 15px;
  cursor: pointer;
  color: @textGray;
  line-height: 1;
  user-select: none;
  &:hover {
    color: #4e5969;
  }
}

:deep(.ant-btn-primary) {
  background: @primaryBlue;
  border-color: @primaryBlue;
  border-radius: @radiusSm;
}
:deep(.ant-btn) {
  border-radius: @radiusSm;
}
</style>
